import React, {
  ComponentProps,
  forwardRef,
  useLayoutEffect,
  useId,
  useRef,
  useState,
} from "react";
import { always, toggle } from "src/utils";
import { CpIconDynamic } from "../cp-icon/cp-icon.component";
import { RenderIconDynamic } from "../cp-icon/render-icon-dynamic";
import { CpBadge, CpBadgeProps } from "../cp-badge/cp-badge";

export type CpCardButtonProps = ComponentProps<"button"> & {
  badge?: Omit<CpBadgeProps, "children" | "ref"> & {
    text: string;
  };
  icon?: CpIconDynamic;
  selected?: boolean;
};

export const CpCardButton = forwardRef<HTMLButtonElement, CpCardButtonProps>(
  function CpCardButton(props, ref) {
    const { badge, children, className, disabled, icon, selected, ...rest } =
      props;

    const badgeRef = useRef<HTMLDivElement>(null);
    const badgeId = useId();
    const [minWidth, setMinWidth] = useState(96);

    useLayoutEffect(() => {
      if (badge?.text && badgeRef.current) {
        const badgeWidth = badgeRef.current.offsetWidth;
        const requiredWidth = Math.max(96, badgeWidth + 24); // right position (8px) and some margin (16px)
        setMinWidth(requiredWidth);
      } else {
        setMinWidth(96);
      }
    }, [badge?.text]);

    return (
      <button
        ref={ref}
        className={always(
          "cp-card-l3",
          "!shadow-none relative flex flex-col items-center justify-center gap-1 min-h-[80px] px-2 py-4",
          toggle(
            selected,
            "!border-[var(--cp-color-app-primary)] !bg-[var(--cp-color-app-active-highlight-bg)]",
            "hover:bg-[var(--cp-color-app-border)]",
          ),
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[var(--cp-color-card-bg)]",
          className,
        )}
        disabled={disabled}
        type="button"
        aria-pressed={selected !== undefined ? !!selected : undefined}
        aria-describedby={badge ? badgeId : undefined}
        data-testid="cp-card-button"
        style={{ minWidth: `${minWidth}px` }}
        {...rest}
      >
        {badge && (
          <CpBadge
            ref={badgeRef}
            {...badge}
            intent={badge.intent || "brand"}
            size={badge.size || "md"}
            id={badgeId}
            className={always(
              "absolute -top-3 right-2 whitespace-nowrap",
              badge.className,
            )}
          >
            {badge.text}
          </CpBadge>
        )}
        {icon && (
          <RenderIconDynamic
            icon={icon}
            className="h-6 w-6 text-[var(--cp-color-app-icon)]"
            size={24}
          />
        )}
        <span className="text-[var(--cp-color-app-primary-text)]">
          {children}
        </span>
      </button>
    );
  },
);
